import { createContext, useContext, ReactNode, useMemo } from 'react';
import { getDataMode, type DataMode } from '@/config/env';

// Context value type
interface DataModeContextType {
  mode: DataMode;
  isMock: boolean;
  isApi: boolean;
  isEmpty: boolean;
}

// Create context with undefined default
const DataModeContext = createContext<DataModeContextType | undefined>(
  undefined
);

// Provider props
interface DataModeProviderProps {
  children: ReactNode;
  /**
   * Force a mode instead of reading the environment.
   *
   * The app never passes this - `VITE_DATA_MODE` is the only thing that
   * decides the mode at runtime, and `getDataMode()` folds to a literal at
   * build time so it cannot be varied per render. That is exactly the problem
   * for Storybook: `.storybook/main.ts` defines `VITE_DATA_MODE='mock'` for
   * the whole catalog, so the `empty` and `api` branches of every mode-aware
   * hook are unreachable there. This is the seam that reaches them - see
   * `withAppProvidersIn` / `withAppShellAt` in `src/stories/decorators.tsx`.
   */
  mode?: DataMode;
}

/**
 * DataModeProvider - Provides data mode context to the entire app
 *
 * Reads VITE_DATA_MODE from environment once at startup.
 * No runtime switching - mode is determined by environment variable, unless a
 * `mode` prop is passed to override it (Storybook only; see the prop's doc).
 *
 * Usage:
 * ```tsx
 * // In App.tsx
 * <DataModeProvider>
 *   <App />
 * </DataModeProvider>
 *
 * // In components
 * const { isMock, isApi } = useDataMode()
 * ```
 */
export function DataModeProvider({
  children,
  mode: override,
}: DataModeProviderProps) {
  // Read mode once from environment - memoized since it never changes
  const contextValue = useMemo<DataModeContextType>(() => {
    const mode = override ?? getDataMode();
    return {
      mode,
      isMock: mode === 'mock',
      isApi: mode === 'api',
      isEmpty: mode === 'empty',
    };
  }, [override]);

  return (
    <DataModeContext.Provider value={contextValue}>
      {children}
    </DataModeContext.Provider>
  );
}

/**
 * useDataMode - Hook to access data mode context
 *
 * Returns the current data mode and helper booleans.
 * Must be used within a DataModeProvider.
 *
 * @returns {DataModeContextType} Object containing:
 *   - mode: 'empty' | 'mock' | 'api'
 *   - isMock: boolean
 *   - isApi: boolean
 *   - isEmpty: boolean
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isMock, isApi } = useDataMode()
 *
 *   if (isApi) {
 *     return <ApiContent />
 *   }
 *
 *   if (isMock) {
 *     return <MockContent />
 *   }
 *
 *   return <EmptyState />
 * }
 * ```
 */
export function useDataMode(): DataModeContextType {
  const context = useContext(DataModeContext);

  if (context === undefined) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }

  return context;
}

// Export types
export type { DataModeContextType };
